import { useMemo } from 'react';
import { useUnit } from 'effector-react';
import clsx from 'clsx';
import { $profileFeatureStore } from '@lib/store';
import { useCardBalance, useCurrencySymbol } from '@shared/hooks';
import { FormatUtils, AmountUtils } from '@shared/utils';
import { type CardsResponse, CardStatus } from '@shared/api/rest/cards';
import { Icon } from '@shared/widgets';
import VisaIcon from '@assets/icons/card/visa.svg';
import styles from './cardGridViewItem.module.css';

interface ICardGridViewItemProps {
  card: CardsResponse;
}

export const CardGridViewItem = (props: ICardGridViewItemProps) => {
  const profileFeatureStore = useUnit($profileFeatureStore.store);

  const symbol = useCurrencySymbol(props.card.currency);
  const balance = useCardBalance(props.card.cardId);

  const viewCardDetails = useMemo(
    () => profileFeatureStore.data?.cardFlows?.find((feature) => feature.cardId === props.card.cardId)?.viewCardDetails,
    [profileFeatureStore.data, props.card],
  );
  return (
    <div
      className={clsx(styles['cards-grid__col'], viewCardDetails === false && 'cursor-not-allowed')}
      title={viewCardDetails === false ? 'You do not have permission to view details' : undefined}
    >
      <div
        className={clsx(styles.card, {
          [styles['card-closed']]: props.card.status === CardStatus.CLOSED,
          [styles['card-disabled']]: props.card.status === CardStatus.DELINQUENT || props.card.status === CardStatus.INACTIVE,
          [styles['card-blocked']]: props.card.status === CardStatus.BLOCKED || props.card.status === CardStatus.FROZEN,
        })}
      >
        <svg
          className={clsx(styles['card__ellipse'], styles['card__ellipse_1'])}
          width='122'
          height='87'
          fill='transparent'
          viewBox='0 0 122 87'
          xmlns='http://www.w3.org/2000/svg'
        >
          <ellipse cx='61' cy='43.5' rx='60' ry='42.5' />
        </svg>
        <svg
          className={clsx(styles['card__ellipse'], styles['card__ellipse_2'])}
          width='122'
          height='87'
          fill='transparent'
          viewBox='0 0 122 87'
          xmlns='http://www.w3.org/2000/svg'
        >
          <ellipse cx='61' cy='43.5' rx='60' ry='42.5' />
        </svg>
        <ul className={styles['card__info']}>
          <li>
            <span className={styles['card__info-item']}>{props.card.cardType}</span>
          </li>
          <li>
            <span className={clsx(styles['card__info-item'], styles['card__status'])}>{props.card.status}</span>
          </li>
        </ul>
        <div className={styles['card__amount']}>
          {symbol}
          {typeof balance === 'number' ? AmountUtils.transformToAmountWithCurrency(balance as number) : ''}
        </div>
        <div className={styles['card__bottom']}>
          <div className={styles['left']}>
            <div className={styles['card__number']}>{FormatUtils.toSliceNumber(props.card.cardNumber, 16)}</div>
            <div className={styles['card__name']}>{props.card.cardHolder}</div>
          </div>
          <Icon icon={<VisaIcon />} className={styles['right']} color='white' />
        </div>
      </div>
    </div>
  );
};
